// Type-safe wrappers around each backend endpoint we consume.
// One function per OpenAPI operation we call from the UI.

import type {
  AdminUser,
  MeResponse,
  AdminUserCreateRequest,
  AdminUserPatchRequest,
  Alert,
  AlertList,
  AlertRule,
  AlertRuleInput,
  AlertRuleView,
  BulkAcknowledgeResponse,
  DashboardOverview,
  EmployeeBaseline,
  EmployeeDetail,
  EmployeeList,
  EmployeePatchRequest,
  EmployeeRegisterRequest,
  EmployeeWithGrant,
  HealthOverviewResponse,
  ImportResult,
  MeasurementSeriesResponse,
  Site,
  TemporaryPasswordResponse,
} from "@faceheart/api-types";
import { api, ApiError, tryRefresh } from "./client.js";

// ── auth ─────────────────────────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string, orgCode: string) =>
    api<void>("/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, org_code: orgCode }),
      skipRefresh: true,
    }),
  refresh: () =>
    api<void>("/v1/auth/refresh", { method: "POST", skipRefresh: true }),
  logout: () => api<void>("/v1/auth/logout", { method: "POST" }),
  me: () => api<MeResponse>("/v1/auth/me"),
  changePassword: (oldPassword: string, newPassword: string) =>
    api<void>("/v1/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    }),
};

// ── devices ────────────────────────────────────────────────────────────────
// Device management UI removed 2026-06-04 (deferred — see
// mirror-device-binding-deferred). The backend /v1/devices endpoints remain
// dormant; restore devicesApi here when the feature is rebuilt.

// ── employees ────────────────────────────────────────────────────────────

// ADR-024 search/filter contract + ADR-027 site dimension.
export type EmployeeListParams = {
  page?: number;
  page_size?: number;
  q?: string;
  source?: string;
  bp_group?: string;
  site_id?: string;
};

// CSV export reuses the list filters and adds a measurement time window
// (ADR-025 §1): either a `range` preset or a custom `from`/`to` (YYYY-MM-DD).
// The backend clamps everything to the 90-day retention floor (ADR-026).
export type ExportRange = "7d" | "30d" | "90d";
export type ExportParams = EmployeeListParams & {
  range?: ExportRange;
  from?: string;
  to?: string;
};

export const employeesApi = {
  list: (params: EmployeeListParams) =>
    api<EmployeeList>("/v1/employees?" + qs(params)),
  get: (id: string) => api<EmployeeDetail>(`/v1/employees/${id}`),
  patch: (id: string, body: EmployeePatchRequest) =>
    api<EmployeeDetail>(`/v1/employees/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  baselines: (id: string) =>
    api<EmployeeBaseline[]>(`/v1/employees/${id}/baselines`),
  measurements: (id: string, range: "7d" | "30d" | "90d", metrics?: string[]) =>
    api<MeasurementSeriesResponse>(
      `/v1/employees/${id}/measurements?` +
        qs({ range, metrics: metrics?.join(",") }),
    ),
  // ADR-021 §3 admin single-create. consent is omitted (collected at the mirror,
  // §4). Returns 201 + a measurement_grant the admin flow ignores; dup code → 409.
  register: (body: EmployeeRegisterRequest) =>
    api<EmployeeWithGrant>("/v1/employees/register", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // ADR-021 CSV importer: POST a CSV body, get a per-row result.
  import: (csv: string) =>
    api<ImportResult>("/v1/employees/import", {
      method: "POST",
      headers: { "Content-Type": "text/csv" },
      body: csv,
    }),
  // Face enrollment (SPEC-019 / ADR-028) — admin uploads a face photo for one
  // employee. `frame` is base64 (no data: prefix); `consentVersion` is the
  // admin's PDPA attestation. 422 if the photo fails the quality gate.
  enrollFace: (id: string, frame: string, consentVersion: string) =>
    api<{ enrolled: boolean }>(`/v1/employees/${id}/face`, {
      method: "POST",
      body: JSON.stringify({ frame, consent_version: consentVersion }),
    }),
  // Batch face upload: one photo per employee keyed by employee_code (derived
  // from the filename). Returns a per-row result like the CSV import.
  enrollFaceBatch: (
    items: { employee_code: string; frame: string; consent_version: string }[],
  ) =>
    api<ImportResult>("/v1/employees/face-batch", {
      method: "POST",
      body: JSON.stringify({ items }),
    }),
  // Delete one employee (ADR-027 v9): soft-delete + PDPA face erasure. Measurements
  // are kept. site_admin may only delete own-site employees. 204 on success.
  remove: (id: string) =>
    api<void>(`/v1/employees/${id}`, { method: "DELETE" }),
  // Bulk-delete: soft-delete many at once; cross-site ids are silently skipped for
  // a site_admin. Returns the count actually deleted.
  bulkDelete: (ids: string[]) =>
    api<{ deleted: number }>("/v1/employees/bulk-delete", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
};

// ── measurements: health overview + export ─────────────────────────────────

export const measurementsApi = {
  // ADR-023 health overview: each employee's latest vitals.
  latestOverview: (params: EmployeeListParams) =>
    api<HealthOverviewResponse>("/v1/measurements/latest-overview?" + qs(params)),
  // ADR-025 CSV export. The endpoint streams `text/csv` with a
  // Content-Disposition attachment and writes a mandatory audit row (highest
  // PII outflow). We fetch it as a blob through the authenticated path —
  // credentials:include sends the cookie, and a 401 triggers the same silent
  // refresh + retry the JSON client uses, so an expired access cookie doesn't
  // turn the download into a login redirect. Returns the CSV Blob.
  exportCsv: async (params: ExportParams): Promise<Blob> => {
    const url = "/v1/measurements/export?" + qs(params);
    const fetchCsv = () =>
      fetch(url, {
        credentials: "include",
        headers: { Accept: "text/csv" },
      });
    let res = await fetchCsv();
    if (res.status === 401 && (await tryRefresh())) res = await fetchCsv();
    if (!res.ok) {
      throw new ApiError(res.status, "export_failed", res.statusText);
    }
    return res.blob();
  },
};

// ── alerts + rules ───────────────────────────────────────────────────────

export const alertsApi = {
  list: (params: {
    page?: number;
    page_size?: number;
    severity?: "critical" | "warning" | "info";
    acknowledged?: boolean;
  }) => api<AlertList>("/v1/alerts?" + qs(params)),
  acknowledge: (id: string) =>
    api<Alert>(`/v1/alerts/${id}/acknowledge`, { method: "POST" }),
  acknowledgeBulk: (ids: string[]) =>
    api<BulkAcknowledgeResponse>("/v1/alerts/acknowledge", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
  rules: () => api<AlertRuleView[]>("/v1/alert-rules"),
  replaceRules: (rules: AlertRuleInput[]) =>
    api<AlertRule[]>("/v1/alert-rules", {
      method: "PUT",
      body: JSON.stringify(rules),
    }),
};

// ── dashboard ────────────────────────────────────────────────────────────

export const dashboardApi = {
  overview: (range: "7d" | "30d" | "90d") =>
    api<DashboardOverview>(`/v1/dashboard/overview?range=${range}`),
};

// ── sites (SPEC-017 / ADR-027 — replaced departments) ──────────────────────

export const sitesApi = {
  list: () => api<Site[]>("/v1/sites"),
  create: (name: string) =>
    api<Site>("/v1/sites", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  rename: (id: string, name: string) =>
    api<Site>(`/v1/sites/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  remove: (id: string) =>
    api<void>(`/v1/sites/${id}`, { method: "DELETE" }),
};

// ── admin users (SPEC-012) ──────────────────────────────────────────────────

export const adminUsersApi = {
  list: () => api<AdminUser[]>("/v1/admin/users"),
  create: (body: AdminUserCreateRequest) =>
    api<AdminUser>("/v1/admin/users", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patch: (id: string, body: AdminUserPatchRequest) =>
    api<AdminUser>(`/v1/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  passwordReset: (id: string) =>
    api<TemporaryPasswordResponse>(`/v1/admin/users/${id}/password-reset`, {
      method: "POST",
    }),
};

// ── qs helper ────────────────────────────────────────────────────────────

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }
  return u.toString();
}
